import {observer} from "mobx-react";
import React, { useEffect } from "react";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import {FormFieldLine} from "../../../../../components/form-field-line/form-field-line";
import {Input} from "../../../../../components/inputs/input/input";
import {GroupedOption, SelectDropdownGrouped} from "../../../../../components/inputs/select-dropdown-grouped/select-dropdown-grouped";
import {IOption, SelectDropdown} from "../../../../../components/inputs/select-dropdown/select-dropdown";
import {Toggle} from "../../../../../components/inputs/toggle/toggle";
import Card from "../../../../../components/layouts/card/card";
import ListLayout from "../../../../../components/layouts/list-layout/list-layout";
import DashTranslation from "../../../../../dashboard-translation/dashboard-translaton";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import {IValidationGroup} from "../../../../../hooks/validation-group/validation-group-hook";
import {FEATURES} from "../../../../../models/Plan";
import currentPortalStore from "../../../../../stores/current-portal-store";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import {autoMarkAsDoneOptionsList, processTypeOptionsList} from "../../../../policies/constants";
import AutoRefundSettings from "./auto-refund-settings";
import { C_RMA_AVAILABLE_REASONS, getAvailableReturnReasonsOptions } from "../../../../../models/User";
import ReturnReasonSelect from "./return-reason-select/return-reason-select";
import { REQUIRE_CRMA_FOLLOWUP_QUESTIONS } from "../../../../../services/api/create-rma-service";
import { PROFILE_CAPABILITIES } from "../../../../../models/ProfileCapabilities";
import ResolveCapability from "../../../../../components/resolve-capability/resolve-capability";
import CreateRmaDefaultFeesSection from "./create-rma-default-fees-section";

interface IProps {
    onExpandClick: (expanded: boolean) => void;
    isExpanded: boolean;
    vGroup: IValidationGroup;
    dataName?: string; /** used by Candu to target an element */
}

const CreateRmaSettingsCard: React.FC<IProps> = observer(({ onExpandClick, isExpanded, vGroup, dataName }) => {
    const pageScope = 'dashboard-settings-store';
    
    const createRmaDefaultSettingsEditorStore = currentPortalStore.setting?.createRmaDefaultSettingsEditorStore;
	const returnFeeEditorStore = createRmaDefaultSettingsEditorStore?.returnFee;
	const labelFeeEditorStore = createRmaDefaultSettingsEditorStore?.labelFee
    const settings = createRmaDefaultSettingsEditorStore?.settings;
    
    const { getTranslation } = useDashboardTranslation();
    const _getDashTranslation = (key: string): string => {
        return getTranslation(key, pageScope);
    };
    const autoRefundAllowed = currentPortalStore.isFeatureAllowed(FEATURES.AUTO_REFUND);
    
    const isAutoRefund = createRmaDefaultSettingsEditorStore?.isAutoRefund;
    const selectedIsAutoRefundOption = processTypeOptionsList().find(o => o.value === (isAutoRefund ? 1 : 0));
    const markAsDoneAllowed = currentPortalStore.isFeatureAllowed(FEATURES.MARK_AS_DONE);
    const autoMarkDone = createRmaDefaultSettingsEditorStore?.autoMarkDone;
    const selectedMarkAsDoneOption = autoMarkAsDoneOptionsList().find(o => o.value === (autoMarkDone ? 1 : 0));
    const noDefaultOption: IOption = {
        label: getTranslation('CREATE_RMA_SETTINGS_NO_DEFAULT', pageScope),
        value: '__NULL__'
    }
    const groupedNoDefaultOptions: GroupedOption[] = [{ options:[noDefaultOption] }];
    const resolutionListOptions = groupedNoDefaultOptions.concat(settings?.getResolutionsListOptions() ?? []) as GroupedOption[];
    
    const getSelectedResolution = () => {
        if(!settings){
            return undefined;
        }
        for (let i in resolutionListOptions) {
            const resolution = resolutionListOptions[i].options.find((resolution: IOption) =>
                (resolution.value === '__NULL__' && createRmaDefaultSettingsEditorStore?.resolutionId === null) ||
                resolution.value === createRmaDefaultSettingsEditorStore?.resolutionId
            );
            if (resolution) {
                return resolution;
            }
        }
    }
    
    const returnMethodListOptions =  [noDefaultOption].concat(settings?.getReturnMethodsListOptions() ?? []);
    const selectedReturnMethodListOptions = returnMethodListOptions.find(i =>
        (createRmaDefaultSettingsEditorStore?.returnMethodId === null && i.value === '__NULL__') ||
        i.value === createRmaDefaultSettingsEditorStore?.returnMethodId
    );

    const availableReturnReasonsOptions = getAvailableReturnReasonsOptions(_getDashTranslation);
    const selectedAvailableReturnReasonOption = availableReturnReasonsOptions.find(o => o.value === createRmaDefaultSettingsEditorStore?.availableReturnReasons);

    const requireFuqAnswersOptions: IOption[] = [
        { label: getTranslation('DONT_SHOW_FUQ_DD_OPTION', pageScope), value: REQUIRE_CRMA_FOLLOWUP_QUESTIONS.DONT_SHOW },
        { label: getTranslation('MANDATORY_FUQ_DD_OPTION', pageScope), value: REQUIRE_CRMA_FOLLOWUP_QUESTIONS.MANDATORY },
        { label: getTranslation('OPTIONAL_FUQ_DD_OPTION', pageScope), value: REQUIRE_CRMA_FOLLOWUP_QUESTIONS.OPTIONAL},
    ] ;
    const selectedRequireFuqAnswerOption = requireFuqAnswersOptions.find(o => o.value === createRmaDefaultSettingsEditorStore?.requireCrmaFuq);
    
    useEffect(() => {
        settings?.load();
    }, [settings]);
    
    return (
	    <ResolveCapability capability={PROFILE_CAPABILITIES.INTERNAL_RMA_CREATION}>
		    {({ 
			    isEnabled,
			    enableFeesConfiguration,
			    enableReturnReasonsConfiguration,
			    enableFollowUpQuestionsConfiguration,
			    enableResolutionConfiguration,
			    enableReturnMethodConfiguration,
			    enableRefundAutomationConfiguration,
			    enableMarkAsDoneAutomationConfiguration,
			    enableCustomerNotificationsConfiguration
		    }) => isEnabled && (
		        <Card
		            toolbar={{
		                title: (
		                    <FontBody1 variant={FONT_VARIANT.BOLD}>
		                        <DashTranslation translationKey="CREATE_RMA_SETTINGS" scope={pageScope} />
		                    </FontBody1>
		                )
		            }}
		            expandCallback={onExpandClick}
		            isExpanded={isExpanded}
		            dataName={dataName}
		        >
		            <FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
		                {getTranslation('INTERNAL_RMA_CREATION_SETTINGS', pageScope)}
		            </FontBody2>
		            <FontBody1 color={FONT_COLOR.PRIMARY3_50}>
		                {getTranslation('INTERNAL_RMA_CREATION_SETTINGS_SUBTITLE', pageScope)}
		            </FontBody1>

		            {enableReturnReasonsConfiguration && (
			            <FormFieldLine
			                label={getTranslation('AVAILABLE_RETURN_REASONS', pageScope)}
			                note={getTranslation('AVAILABLE_RETURN_REASONS_INSTRUCTIONS', pageScope)}
			                dataName="available-return-reasons"
			            >
			                <SelectDropdown
			                    options={availableReturnReasonsOptions}
			                    value={selectedAvailableReturnReasonOption}
			                    onChange={(v) => {
			                        createRmaDefaultSettingsEditorStore?.setAvailableReturnReasons(v?.value as C_RMA_AVAILABLE_REASONS);
			                    }}
			                />
			            </FormFieldLine>
		            )}

		            {enableFollowUpQuestionsConfiguration && (
			            <FormFieldLine
			                label={getTranslation('ANSWERING_FUQ_DD_LABEL', pageScope)}
			                note={getTranslation('ANSWERING_FUQ_DD_DESCRIPTION', pageScope)}
			                dataName="answering-fuq"
			            >
			                <SelectDropdown
			                    options={requireFuqAnswersOptions}
			                    value={selectedRequireFuqAnswerOption}
			                    onChange={(v) => {
			                        createRmaDefaultSettingsEditorStore?.setRequireFuq(v?.value as REQUIRE_CRMA_FOLLOWUP_QUESTIONS);
			                    }}
			                />
			            </FormFieldLine>
		            )}


		            <FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
		                {getTranslation('CREATE_RMA_SETTINGS_MANAGE_RMA_DEFAULTS', pageScope)}
		            </FontBody2>
		            <FontBody1 color={FONT_COLOR.PRIMARY3_50}>
		                {getTranslation('CREATE_RMA_SETTINGS_MANAGE_RMA_DEFAULTS_SUBTITLE', pageScope)}
		            </FontBody1>


		            <FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
		                {getTranslation('CREATE_RMA_SETTINGS_ITEM_REQUEST_DEFAULTS', pageScope)}
		            </FontBody2>
		            <FormFieldLine
		                label={getTranslation('CREATE_RMA_SETTINGS_RETURN_REASON', pageScope)}
		                dataName="create-rma-settings-return-reason"
		            >
		                <ReturnReasonSelect noDefaultOption={noDefaultOption} createRmaDefaultSettingsEditorStore={createRmaDefaultSettingsEditorStore} />
		            </FormFieldLine>
		            {enableResolutionConfiguration && (
			            <FormFieldLine
			                label={getTranslation('CREATE_RMA_SETTINGS_RESOLUTION', pageScope)}
			                dataName="create-rma-settings-resolution"
			            >
			                <SelectDropdownGrouped
			                    options={resolutionListOptions}
			                    value={getSelectedResolution()}
			                    onChange={(v) => {
			                        let value : number | string | null = v?.value as number | string;
			                        if(value === '__NULL__') {
			                            value = null;
			                        }
			                        createRmaDefaultSettingsEditorStore?.setResolution(value as number | null)
			                    }}
			                    isSearchable={true}
			                />
			            </FormFieldLine>
		            )}
		            {enableReturnMethodConfiguration && (
			            <FormFieldLine
			                label={getTranslation('CREATE_RMA_SETTINGS_RETURN_METHOD', pageScope)}
			                dataName="create-rma-settings-return-method"
			            >
			                <SelectDropdown
			                    options={returnMethodListOptions}
			                    value={selectedReturnMethodListOptions}
			                    onChange={(v) => {
			                        let value : number | string | null = v?.value as number | string;
			                        if(value === '__NULL__') {
			                            value = null;
			                        }
			                        createRmaDefaultSettingsEditorStore?.setReturnMethod(value as number | null);
			                    }}
			                    isSearchable={true}
			                />
			            </FormFieldLine>
		            )}

			        {enableFeesConfiguration && (
				        <>
					        <FormFieldLine
						        label={getTranslation('CREATE_RMA_SETTINGS_RESTOCKING_FEE', pageScope)}
						        dataName="create-rma-settings-restocking-fee"
					        >
						        <Input temp value={createRmaDefaultSettingsEditorStore?.restockingFee} min={0} onChange={createRmaDefaultSettingsEditorStore?.setRestockingFee} type={'number'}/>
					        </FormFieldLine>

					        <FormFieldLine
						        label={getTranslation('CREATE_RMA_SETTINGS_ADJUSTMENTS', pageScope)}
						        dataName="create-rma-settings-adjustments"
					        >
						        <Input temp value={createRmaDefaultSettingsEditorStore?.adjustment} onChange={createRmaDefaultSettingsEditorStore?.setAdjustment} type={'number'}/>
					        </FormFieldLine>
				        </>
			        )}

		            {enableFeesConfiguration && returnFeeEditorStore && labelFeeEditorStore && (
			            <CreateRmaDefaultFeesSection
			                returnFeeEditorStore={returnFeeEditorStore}
			                labelFeeEditorStore={labelFeeEditorStore}
			                validationGroup={vGroup}
			            />
		            )}

		            <FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
		                {getTranslation('CREATE_RMA_SETTINGS_AUTOMATION_DEFAULTS', pageScope)}
		            </FontBody2>

		            {enableRefundAutomationConfiguration && (
			            <FormFieldLine
			                label={getTranslation('CREATE_RMA_SETTINGS_AUTO_REFUND', pageScope)}
			                dataName="create-rma-settings-auto-refund"
			            >
			                <ListLayout>
			                <SelectDropdown
			                    value={selectedIsAutoRefundOption}
			                    options={processTypeOptionsList()}
			                    onChange={v => createRmaDefaultSettingsEditorStore?.setAutoRefund(!!v?.value)}
			                    disabled={!autoRefundAllowed}
			                    isSearchable={false}
			                />
			                     { isAutoRefund &&
			                         <AutoRefundSettings store={createRmaDefaultSettingsEditorStore} validationGroup={vGroup} />

			                     }
			                </ListLayout>
			            </FormFieldLine>
		            )}

		            {enableMarkAsDoneAutomationConfiguration && (
			            <FormFieldLine
			                label={getTranslation('CREATE_RMA_SETTINGS_AUTO_MARK_AS_DONE', pageScope)}
			                dataName="create-rma-settings-auto-mark-as-done"
			            >
			                <SelectDropdown
			                    value={selectedMarkAsDoneOption}
			                    onChange={(v) => createRmaDefaultSettingsEditorStore?.setAutoMarkDone(v?.value === 1)}
			                    disabled={!markAsDoneAllowed}
			                    options={autoMarkAsDoneOptionsList()}
			                />
			            </FormFieldLine>
		            )}


		            {enableCustomerNotificationsConfiguration && (
			            <>
				            <FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>
				                {getTranslation('CREATE_RMA_SETTINGS_CUSTOMER_NOTIFICATION_DEFAULTS_HEADER', pageScope)}
				            </FontBody2>
				             <FormFieldLine
				                 label={getTranslation('CREATE_RMA_SETTINGS_SEND_CUSTOMER_NOTIFICATION', pageScope)}
				                 note={getTranslation('CREATE_RMA_SETTINGS_SEND_CUSTOMER_NOTIFICATION_NOTE', pageScope)}
				                 dataName="create-rma-settings-send-customer-notification"
				             >
				                 <Toggle
				                     label={''}
				                     checked={createRmaDefaultSettingsEditorStore?.sendCustomerEmails}
				                     onChange={createRmaDefaultSettingsEditorStore?.setSendCustomerEmails}
				                 />
				            </FormFieldLine>
			            </>
		            )}
		        </Card>
		    )}
		</ResolveCapability>
    );
});

export default CreateRmaSettingsCard;
